import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { FileDrop } from '@/components/FileDrop';
import { canBeAssigned } from '@/features/access/signIn';
import { Modal } from '@/components/Modal';
import { MobileList } from '@/components/MobileList';
import { Pill, PillRow } from '@/components/Pill';
import { DataToolbar, NoResults, SortableTh } from '@/components/table';
import { Badge, Card, ErrorNotice, Field, PageHeader, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api, ApiError, type Job, type Site } from '@/lib/api';
import { dateShort, fileSize, fromMilan, time } from '@/lib/format';
import { useTableState, type SortOption } from '@/lib/table';
import { useLabel } from '@/lib/useLabel';
import { Combobox } from '@/components/Combobox';
import { PeopleMultiSelect, siteOptions } from '@/features/access/people';

const FILTERS = [
  { value: '', labelKey: null },
  { value: 'scheduled', labelKey: 'scheduled' },
  { value: 'in_progress', labelKey: 'in_progress' },
  { value: 'on_hold', labelKey: 'on_hold' },
  { value: 'completed', labelKey: 'completed' },
];

const JOB_SORTS: SortOption<Job>[] = [
  { key: 'scheduled', labelKey: 'jobs.scheduled', value: (j) => j.scheduledStart, defaultDirection: 'desc' },
  { key: 'reference', labelKey: 'jobs.reference', value: (j) => j.reference, defaultDirection: 'desc' },
  { key: 'title', labelKey: 'jobs.jobTitle', value: (j) => j.title },
  { key: 'client', labelKey: 'jobs.client', value: (j) => j.client?.name },
  { key: 'status', labelKey: 'common.status', value: (j) => j.status },
];

export function JobsPage() {
  const { t } = useI18n();
  const L = useLabel();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    siteId: '',
    clientId: '',
    scheduledStart: '',
    scheduledEnd: '',
    priority: 'normal',
    /** '' = follow the global setting, 'yes' / 'no' = pin it for this job. */
    allowEarly: '',
    allowLate: '',
  });

  /*
   * Files chosen before the job exists.
   *
   * They cannot be uploaded yet — a document has to hang off something — so they are
   * held here and uploaded once the job has an id. The app already showed job
   * attachments; the portal had no way to put any there, which is the gap this closes.
   */
  const [attachments, setAttachments] = useState<File[]>([]);

  /*
   * Who does the work, chosen with the job rather than on a second screen. A job
   * nobody is on does not appear in anyone's app, and "I created it but nobody sees
   * it" is the first thing that happens otherwise.
   */
  const [assignees, setAssignees] = useState<string[]>([]);
  /** Draft = not confirmed yet: visible in the app but nobody can clock in. */
  const [asDraft, setAsDraft] = useState(false);
  const people = useQuery({ queryKey: ['users'], queryFn: () => api.users() });
  const assignable = (people.data?.users ?? []).filter(canBeAssigned);

  const jobs = useQuery({
    queryKey: ['jobs', status],
    queryFn: () => api.jobs(status || undefined),
  });
  const sites = useQuery({ queryKey: ['sites'], queryFn: api.sites });

  // Reference, title, client, site and crew are all things someone remembers a job by;
  // whichever one they type should find it.
  const table = useTableState<Job>({
    rows: jobs.data?.jobs ?? [],
    search: (job) => [
      job.reference,
      job.title,
      job.client?.name,
      job.site?.name,
      job.site?.city,
      ...job.teamNames,
      L('job', job.status).label,
    ],
    sorts: JOB_SORTS,
    defaultSortKey: 'scheduled',
    storageKey: 'jobs',
  });

  const create = useMutation({
    mutationFn: async () => {
      const site = (sites.data?.sites ?? []).find((s: Site) => s.id === form.siteId);
      const created = await api.createJob({
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        siteId: form.siteId || undefined,
        // Inherit the client from the site — asking for it twice is how they diverge.
        clientId: site?.client?.id ?? undefined,
        priority: form.priority,
        scheduledStart: form.scheduledStart ? fromMilan(form.scheduledStart) : undefined,
        scheduledEnd: form.scheduledEnd ? fromMilan(form.scheduledEnd) : undefined,
        allowClockInBeforeStart: form.allowEarly === '' ? null : form.allowEarly === 'yes',
        allowClockOutAfterEnd: form.allowLate === '' ? null : form.allowLate === 'yes',
      });

      /*
       * Attachments go up one at a time, after the job exists.
       *
       * Sequentially rather than in parallel: these are drawings over site broadband,
       * and six concurrent 20 MB uploads is how you get six timeouts instead of six
       * files. `drawing` is the default category because that is what a job carries
       * more often than anything else; it can be corrected in the library.
       */
      // Confirmed unless kept as a draft: a draft job cannot be clocked into.
      if (!asDraft) await api.setJobStatus(created.job.id, 'scheduled');
      // Assigning is what puts it in their app and sends them the notification.
      if (assignees.length > 0) await api.assignJob(created.job.id, assignees);

      for (const file of attachments) {
        await api.uploadDocument(file, {
          category: 'drawing',
          jobId: created.job.id,
          visibility: 'assigned_engineers',
        });
      }

      return created;
    },
    onSuccess: async () => {
      setCreating(false);
      setAttachments([]);
      setAssignees([]);
      setAsDraft(false);
      setForm({
        title: '',
        description: '',
        siteId: '',
        clientId: '',
        scheduledStart: '',
        scheduledEnd: '',
        priority: 'normal',
        allowEarly: '',
        allowLate: '',
      });
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });

  return (
    <>
      <PageHeader
        title={t('jobs.title')}
        subtitle={t('jobs.subtitle')}
        actions={
          <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
            {t('jobs.createJob')}
          </button>
        }
      />

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        size="lg"
        title={t('jobs.createJob')}
        footer={
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={!form.title.trim() || !form.siteId || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? t('common.creating') : t('jobs.createJob')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setCreating(false)}>
              {t('common.cancel')}
            </button>

            {create.error ? (
              <p className="text-sm text-danger ms-auto">
                {create.error instanceof ApiError ? create.error.message : t('common.saveError')}
              </p>
            ) : null}
          </>
        }
      >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="col-span-2">
              <Field label={t('jobs.jobTitle')}>
                <input
                  className="input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={t('jobs.descriptionPlaceholder')}
                />
              </Field>
            </div>
            <div className="col-span-2">
              <Field label={t('common.description')}>
                <textarea
                  className="input h-24 py-2 resize-none"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </Field>
            </div>
            <Field label={t('common.site')}>
              <Combobox
                value={form.siteId}
                onChange={(siteId) => setForm({ ...form, siteId })}
                options={siteOptions(sites.data?.sites ?? [])}
              />
            </Field>
            <Field label={t('common.priority')}>
              <select
                className="input"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              >
                <option value="normal">{t('common.normal')}</option>
                <option value="urgent">{t('jobs.urgent')}</option>
              </select>
            </Field>
            <Field label={t('jobs.scheduledStart')}>
              <input
                className="input"
                type="datetime-local"
                value={form.scheduledStart}
                onChange={(e) => setForm({ ...form, scheduledStart: e.target.value })}
              />
            </Field>
            <Field label={t('jobs.scheduledEnd')}>
              <input
                className="input"
                type="datetime-local"
                value={form.scheduledEnd}
                onChange={(e) => setForm({ ...form, scheduledEnd: e.target.value })}
              />
            </Field>

            {/*
              Clock-in rules, next to the times they are about.
              Hidden entirely when there is no planned time — a rule about clocking in
              "early" is meaningless without something to be early for, and showing a
              dead control is worse than showing none.
            */}
            {form.scheduledStart || form.scheduledEnd ? (
              <div className="col-span-2 pt-3 border-t border-border">
                <div className="label-text mb-1">{t('jobs.workingHours')}</div>
                <p className="text-xs text-muted mb-3 leading-relaxed">
                  {t('jobs.workingHoursHint')}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label={t('jobs.earlyLabel')}>
                    <select
                      className="input"
                      value={form.allowEarly}
                      onChange={(e) => setForm({ ...form, allowEarly: e.target.value })}
                    >
                      <option value="">{t('jobs.useGlobal')}</option>
                      <option value="yes">{t('jobs.allowIt')}</option>
                      <option value="no">{t('jobs.blockIt')}</option>
                    </select>
                  </Field>

                  <Field label={t('jobs.lateLabel')}>
                    <select
                      className="input"
                      value={form.allowLate}
                      onChange={(e) => setForm({ ...form, allowLate: e.target.value })}
                    >
                      <option value="">{t('jobs.useGlobal')}</option>
                      <option value="yes">{t('jobs.allowIt')}</option>
                      <option value="no">{t('jobs.blockIt')}</option>
                    </select>
                  </Field>
                </div>
              </div>
            ) : null}

            {/* Attachments. The app has always shown these; the portal could not add any. */}
            <div className="col-span-2 pt-3 border-t border-border">
              <Field label={t('jobs.attachments')} hint={t('jobs.attachmentsHint')} group>
                <div className="space-y-2">
                  {attachments.map((file, i) => (
                    <div
                      key={`${file.name}-${i}`}
                      className="flex items-center gap-3 px-3 py-2 rounded border border-border bg-surface-alt"
                    >
                      <span className="text-sm truncate flex-1">{file.name}</span>
                      <span className="text-xs text-subtle tabular whitespace-nowrap">
                        {fileSize(file.size)}
                      </span>
                      <button
                        type="button"
                        className="text-sm text-primary hover:underline"
                        onClick={() => setAttachments(attachments.filter((_, n) => n !== i))}
                      >
                        {t('common.remove')}
                      </button>
                    </div>
                  ))}

                  <FileDrop onPick={(file) => setAttachments([...attachments, file])} />
                </div>
              </Field>
            </div>

            <div className="col-span-2 pt-3 border-t border-border">
              <Field label={t('jobs.assignTo')} hint={t('jobs.assignToHint')} group>
                <PeopleMultiSelect users={assignable} selected={assignees} onChange={setAssignees} />
              </Field>
              <label className="flex items-start gap-2 mt-3 cursor-pointer">
                <input type="checkbox" className="mt-1" checked={asDraft} onChange={(e) => setAsDraft(e.target.checked)} />
                <span className="text-sm">
                  {t('jobs.saveAsDraft')}
                  <span className="block text-xs text-subtle">{t('jobs.saveAsDraftHint')}</span>
                </span>
              </label>
            </div>
          </div>

      </Modal>

      <PillRow className="mb-4">
        {FILTERS.map((f) => (
          <Pill key={f.value} active={status === f.value} onClick={() => setStatus(f.value)}>
            {f.labelKey ? L('job', f.labelKey).label : t('common.all')}
          </Pill>
        ))}
      </PillRow>

      {jobs.isLoading ? (
        <Spinner />
      ) : jobs.error ? (
        <ErrorNotice error={jobs.error} />
      ) : (
        <>
          <DataToolbar
            state={table}
            searchPlaceholder={t('jobs.searchPlaceholder')}
            countOne="jobs.countOne"
            countMany="jobs.countMany"
          />

          <Card padded={false}>
          <div className="lg:hidden p-2">
            <MobileList<Job>
              rows={table.rows}
              keyOf={(j) => j.id}
              title={(j) => j.title}
              subtitle={(j) => `${j.reference} \u00b7 ${j.client?.name ?? '\u2014'}`}
              badges={(j) => (
                <>
                  <Badge tone={L('job', j.status).tone}>{L('job', j.status).label}</Badge>
                  {j.priority === 'urgent' ? <Badge tone="danger">{t('jobs.urgent')}</Badge> : null}
                </>
              )}
              fields={[
                { label: t('common.site'), value: (j) => j.site?.city ?? null },
                {
                  label: t('jobs.scheduled'),
                  value: (j) =>
                    j.scheduledStart
                      ? `${dateShort(j.scheduledStart)} ${time(j.scheduledStart)}`
                      : null,
                },
                {
                  label: t('jobs.team'),
                  value: (j) =>
                    j.teamNames.length > 0 ? (
                      j.teamNames.join(', ')
                    ) : (
                      <span className="text-danger font-semibold">{t('common.none')}</span>
                    ),
                },
              ]}
              action={(j) => (
                <Link to={`/jobs/${j.id}`} className="btn-primary">
                  {t('common.open')}
                </Link>
              )}
            />
          </div>

          <div className="table-scroll max-lg:hidden">
            <table className="table-wide">
              <thead>
                <tr>
                  <SortableTh state={table} sortKey="reference">
                    {t('jobs.reference')}
                  </SortableTh>
                  <SortableTh state={table} sortKey="title">
                    {t('jobs.jobTitle')}
                  </SortableTh>
                  <SortableTh state={table} sortKey="client">
                    {t('jobs.client')}
                  </SortableTh>
                  <th className="th">{t('common.site')}</th>
                  <SortableTh state={table} sortKey="scheduled">
                    {t('jobs.scheduled')}
                  </SortableTh>
                  <th className="th">{t('jobs.team')}</th>
                  <SortableTh state={table} sortKey="status">
                    {t('common.status')}
                  </SortableTh>
                </tr>
              </thead>
              <tbody>
                {table.rows.map((job) => {
                  const s = L('job', job.status);
                  return (
                    <tr key={job.id} className="hover:bg-surface-alt">
                      <td className="td tabular text-muted whitespace-nowrap">{job.reference}</td>
                      <td className="td">
                        <Link to={`/jobs/${job.id}`} className="font-semibold hover:text-primary">
                          {job.title}
                        </Link>
                        {job.priority === 'urgent' ? (
                          <Badge tone="danger">
                            <span className="ms-1">{t('jobs.urgent')}</span>
                          </Badge>
                        ) : null}
                      </td>
                      <td className="td text-muted">{job.client?.name ?? '—'}</td>
                      <td className="td text-muted">{job.site?.city ?? '—'}</td>
                      <td className="td tabular text-muted whitespace-nowrap">
                        {job.scheduledStart
                          ? `${dateShort(job.scheduledStart)} ${time(job.scheduledStart)}`
                          : '—'}
                      </td>
                      <td className="td text-muted text-sm">
                        {job.teamNames.length > 0 ? (
                          job.teamNames.join(', ')
                        ) : (
                          <span className="text-danger font-semibold">{t('common.none')}</span>
                        )}
                      </td>
                      <td className="td">
                        <Badge tone={s.tone}>{s.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {table.rows.length === 0 ? <NoResults onClear={table.clear} /> : null}
          </Card>
        </>
      )}
    </>
  );
}
